import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
  Button,
  Stack,
  useMediaQuery,
  Avatar,
  Chip,
} from '@mui/material';
import {
  AutoAwesome,
  Co2Outlined,
  PrecisionManufacturing,
  TrendingUp,
  Support,
  Verified,
} from '@mui/icons-material';
import { getCompanyInfo, getCompanyStats, getTestimonials, CompanyInfo, CompanyStats, Testimonial } from '../api/publicApi';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../config/brand';
import { BrandHero } from '../components/brand/BrandHero';

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  image: string;
  department: string;
}

const teamMembers: TeamMember[] = [
  {
    name: 'John Smith',
    role: 'CEO & Founder',
    bio: 'Visionary leader with over 20 years of manufacturing experience, driving innovation and sustainable growth.',
    image: BRAND.images.teamSign,
    department: 'Leadership',
  },
  {
    name: 'Jane Doe',
    role: 'Chief Operations Officer',
    bio: 'Operations expert specializing in process optimization and supply chain management.',
    image: BRAND.images.teamFacility,
    department: 'Operations',
  },
  {
    name: 'Mike Johnson',
    role: 'Chief Technology Officer',
    bio: 'Technology guru and innovation driver, leading our digital transformation initiatives.',
    image: BRAND.images.gateSign,
    department: 'Technology',
  },
  {
    name: 'Sarah Williams',
    role: 'Head of Quality Assurance',
    bio: 'Quality assurance specialist with a focus on excellence and continuous improvement.',
    image: BRAND.images.outdoorSign,
    department: 'Quality',
  },
  {
    name: 'David Chen',
    role: 'Head of Production',
    bio: 'Production expert with deep knowledge of manufacturing processes and efficiency optimization.',
    image: BRAND.images.facilityExterior,
    department: 'Production',
  },
  {
    name: 'Lisa Rodriguez',
    role: 'Head of Sustainability',
    bio: 'Sustainability champion driving our environmental initiatives and green manufacturing practices.',
    image: BRAND.images.furniture,
    department: 'Sustainability',
  },
];

interface Value {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const ourValues: Value[] = [
  {
    title: 'Innovation',
    description: 'Constantly pushing the boundaries of manufacturing technology and processes',
    icon: <AutoAwesome sx={{ fontSize: 40 }} />,
    color: '#6366f1',
  },
  {
    title: 'Quality',
    description: 'Uncompromising commitment to excellence in every product we manufacture',
    icon: <Verified sx={{ fontSize: 40 }} />,
    color: '#f59e0b',
  },
  {
    title: 'Sustainability',
    description: 'Environmental responsibility and sustainable manufacturing practices',
    icon: <Co2Outlined sx={{ fontSize: 40 }} />,
    color: '#10b981',
  },
  {
    title: 'Excellence',
    description: 'Striving for perfection in every aspect of our operations',
    icon: <PrecisionManufacturing sx={{ fontSize: 40 }} />,
    color: '#06b6d4',
  },
  {
    title: 'Collaboration',
    description: 'Working together with partners and customers to achieve mutual success',
    icon: <Support sx={{ fontSize: 40 }} />,
    color: '#8b5cf6',
  },
  {
    title: 'Growth',
    description: 'Continuous improvement and expansion of our capabilities',
    icon: <TrendingUp sx={{ fontSize: 40 }} />,
    color: '#ef4444',
  },
];

const AboutPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [info, stats, test] = await Promise.all([
          getCompanyInfo(),
          getCompanyStats(),
          getTestimonials(),
        ]);
        setCompanyInfo(info);
        setCompanyStats(stats);
        setTestimonials(test);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: BRAND.colors.creamLight, minHeight: '100vh' }}>
      <BrandHero
        title={`About ${BRAND.name}`}
        subtitle={BRAND.tagline}
        description={companyInfo?.description || 'A leading African manufacturing company specializing in textiles, furniture, metalworks, and footwear.'}
        backgroundImage={BRAND.images.outdoorSign}
        primaryAction={{ label: 'Our Story', onClick: () => navigate('/about/story') }}
        secondaryAction={{ label: 'Meet Our Team', onClick: () => navigate('/about/team') }}
      />

      {/* Company Stats */}
      {companyStats && (
        <Box sx={{ py: 8, background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
          <Container maxWidth="lg">
            <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6, fontWeight: 700 }}>
              Our Impact in Numbers
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={6} md={3}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  }}
                >
                  <Typography variant="h2" sx={{ fontWeight: 800, color: '#6366f1' }}>
                    {companyStats.yearsExperience}+
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Years Experience
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  }}
                >
                  <Typography variant="h2" sx={{ fontWeight: 800, color: '#06b6d4' }}>
                    {companyStats.projectsCompleted}+
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Projects Completed
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  }}
                >
                  <Typography variant="h2" sx={{ fontWeight: 800, color: '#10b981' }}>
                    {companyStats.happyCustomers}+
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Happy Customers
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  }}
                >
                  <Typography variant="h2" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                    {companyStats.teamMembers}+
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Team Members
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}

      {/* Our Story */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
              Our Story
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4 }}>
              Founded in {companyInfo?.founded || 2020}, Monio has grown from a small workshop to a leading manufacturing company in Africa.
            </Typography>
            <Typography paragraph sx={{ mb: 3 }}>
              Our journey began with a simple vision: to revolutionize manufacturing in Africa through innovation, quality, and sustainability. 
              What started as a small team of passionate craftsmen has evolved into a comprehensive manufacturing powerhouse.
            </Typography>
            <Typography paragraph sx={{ mb: 4 }}>
              Today, we serve clients across {companyStats?.countriesServed || 15} countries, delivering premium products and services 
              that meet international standards while contributing to Africa's industrial development.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Chip label="Innovation" color="primary" />
              <Chip label="Quality" color="secondary" />
              <Chip label="Sustainability" color="success" />
              <Chip label="Excellence" color="warning" />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Our Mission
              </Typography>
              <Typography paragraph>
                {companyInfo?.mission || 'To revolutionize manufacturing through innovation and excellence'}
              </Typography>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Our Vision
              </Typography>
              <Typography paragraph>
                {companyInfo?.vision || 'To be the leading manufacturing company in Africa'}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Our Values */}
      <Box sx={{ py: 10, background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6, fontWeight: 700 }}>
            Our Values
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: '800px', mx: 'auto' }}>
            The principles that guide everything we do
          </Typography>
          <Grid container spacing={4}>
            {ourValues.map((value) => (
              <Grid item xs={12} md={6} lg={4} key={value.title}>
                <Card
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ color: value.color }}>
                      {value.icon}
                    </Box>
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {value.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Team */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6, fontWeight: 700 }}>
          Meet Our Leadership Team
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: '800px', mx: 'auto' }}>
          The passionate individuals driving our success
        </Typography>
        <Grid container spacing={4}>
          {teamMembers.map((member) => (
            <Grid item xs={12} md={6} lg={4} key={member.name}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Avatar
                  src={member.image}
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 3,
                    background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {member.name}
                </Typography>
                <Typography variant="h6" color="primary" gutterBottom>
                  {member.role}
                </Typography>
                <Chip label={member.department} size="small" sx={{ mb: 2 }} />
                <Typography color="text.secondary">
                  {member.bio}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <Box sx={{ py: 10, background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)', color: 'white' }}>
          <Container maxWidth="lg">
            <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6, fontWeight: 700 }}>
              What Our Clients Say
            </Typography>
            <Grid container spacing={4}>
              {testimonials.map((testimonial) => (
                <Grid item xs={12} md={4} key={testimonial.id}>
                  <Card
                    sx={{
                      p: 4,
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 3,
                      color: 'white',
                    }}
                  >
                    <Typography paragraph sx={{ fontStyle: 'italic', mb: 3 }}>
                      "{testimonial.content}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={testimonial.image} sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {testimonial.role}, {testimonial.company}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Call to Action */}
      <Box sx={{ py: 10, background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
              Ready to Work with Us?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
              Join hundreds of satisfied clients who trust Monio for their manufacturing needs
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Get Started Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
